import React from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

const routerFutureFlags = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;
import { SideNav } from '../components';
import { useAuth } from '../hooks/auth';
import AccessGroup from '../pages/AccessGroup';
import AccessDenied from '../pages/AccessDenied';
import Addresses from '../pages/Address';
import ClientCreateEdit from '../pages/Clients/CreateEdit';
import ClientReportBirthdaysPrint from '../pages/Clients/Reports/Print/Birthdays';
import ClientReportListPrint from '../pages/Clients/Reports/Print/List';
import ClientReportBirthdays from '../pages/Clients/Reports/View/Birthdays';
import ClientReportList from '../pages/Clients/Reports/View/List';
import Clients from '../pages/Clients';
import Companies from '../pages/Companies';
import Company from '../pages/Company';
import WhatsappConnections from '../pages/Connections';
import WhatsappMonitoring from '../pages/WhatsappMonitoring';
import Covenants from '../pages/Covenants';
import Dashboard from '../pages/Dashboard';
import DentalTreatmentApprovalOrNot from '../pages/Dental/ApprovalOrNot';
import DentalCreateEdit from '../pages/Dental/CreateEdit';
import DentalTreatmentEvolutionPrint from '../pages/Dental/Evolution/Print';
import DentalTreatmentProcedurePrint from '../pages/Dental/Evolution/ProcedurePrint';
import DentalTreatmentEvolution from '../pages/Dental/Evolution';
import DentalTreatmentPrint from '../pages/Dental/Print';
import Dental from '../pages/Dental';
import DocumentCreateEdit from '../pages/Documents/CreateEdit';
import DocumentPrint from '../pages/Documents/Print';
import Documents from '../pages/Documents';
import FinancialCashFlow from '../pages/Financials/Reports/CashFlow';
import FinancialAdvancedReports from '../pages/Financials/Reports/Advanced';
import CommissionClosingPage from '../pages/Financials/Reports/CommissionClosing';
import DelinquencyPage from '../pages/Financials/Reports/Delinquency';
import FinancialDRE from '../pages/Financials/Reports/Dre';
import MovementsFinancials from '../pages/Financials/Reports/MovementsFinancials';
import AccountsPayable from '../pages/Financials/Reports/AccountsPayable';
import AccountsReceivable from '../pages/Financials/Reports/AccountsReceivable';
import CashRegisterPage from '../pages/Financials/CashRegister';
import FinancialCreateEdit from '../pages/Financials/CreateEdit';
import Financials from '../pages/Financials';
import FinancialPlans from '../pages/FinancialPlans';
import FormCharts from '../pages/Forms/Charts';
import FormClientEvolution from '../pages/Forms/Evolution';
import ModelFormCreateEdit from '../pages/ModelsForms/CreateEdit';
import FormClientPrint from '../pages/Forms/Print';
import FormClientQuiz from '../pages/Forms/Quiz';
import Forms from '../pages/Forms';
import Guides from '../pages/Guides';
import GuidesStatus from '../pages/GuidesStatus';
import HealthCertificateCreateEdit from '../pages/HealthCertificates/CreateEdit';
import HealthCertificatePrint from '../pages/HealthCertificates/Print';
import HealthCertificates from '../pages/HealthCertificates';
import Holidays from '../pages/Holidays';
import Inventories from '../pages/Inventories';
import InventoryEntries from '../pages/InventoryEntries';
import InventoryOutputs from '../pages/InventoryOutputs';
import ModelDocumentCreateEdit from '../pages/ModelsDocuments/CreateEdit';
import ModelsDocuments from '../pages/ModelsDocuments';
import ModelHealthCertificateCreateEdit from '../pages/ModelsHealthCertificates/CreateEdit';
import ModelsHealthCertificates from '../pages/ModelsHealthCertificates';
import ModelsForms from '../pages/ModelsForms';
import PageNotFound from '../pages/PageNotFound';
import PatientDocumentPrint from '../pages/PatientDocumentPrint';
import PatientHealthCertificatePrint from '../pages/PatientHealthCertificatePrint';
import PatientLogin from '../pages/PatientLogin';
import PatientPortal from '../pages/PatientPortal';
import PaymentsMethods from '../pages/PaymentsMethods';
import Procedures from '../pages/Procedures';
import ProceduresReportPage from '../pages/Treatments/Report/Procedures';
import Profile from '../pages/Profile';
import ProductsCategories from '../pages/ProductsCategories';
import Products from '../pages/Products';
import ProfessionalCreateEdit from '../pages/Professionals/CreateEdit';
import Professionals from '../pages/Professionals';
import PublicSchedule from '../pages/PublicSchedule';
import QuickSalePage from '../pages/QuickSale';
import SalesPage from '../pages/Sales';
import ScheduleConfirm from '../pages/ScheduleConfirm';
import SchedulesPrint from '../pages/Schedules/Print';
import Schedules from '../pages/Schedules';
import SchedulesStatus from '../pages/SchedulesStatus';
import SignIn from '../pages/SignIn';
import SignatureValidation from '../pages/SignatureValidation';
import Specialties from '../pages/Specialties';
import Taxes from '../pages/Taxes';
import Terminologies from '../pages/Terminologies';
import TreatmentApprovalOrNot from '../pages/Treatments/ApprovalOrNot';
import TratmentCreateEdit from '../pages/Treatments/CreateEdit';
import TreatmentEvolutionPrint from '../pages/Treatments/Evolution/Print';
import TreatmentEvolution from '../pages/Treatments/Evolution';
import TreatmentPrint from '../pages/Treatments/Print';
import TreatmentsProceduresReportPage from '../pages/Treatments/Report/Procedures';
import TreatmentsReportPage from '../pages/Treatments/Report/treatments';
import Treatments from '../pages/Treatments';
import UserCreateEdit from '../pages/Users/CreateEdit';
import Users from '../pages/Users';
import WhatsappUsage from '../pages/Whatsapp';
import CrmCampaigns from '../pages/CRM/Campaigns';
import CrmCampaignsEdit from '../pages/CRM/Campaigns/Edit';
import CrmRecipientsModal from '../pages/CRM/Campaigns/RecipientsModal';
import CrmTemplates from '../pages/CRM/Templates';
import CrmTemplatesEdit from '../pages/CRM/Templates/Edit';
import CrmMessaging from '../pages/CRM/Messaging';
import CrmSendModal from '../pages/CRM/Messaging/SendModal';
import CrcDashboard from '../pages/CRC/Dashboard';
import CrcContacts from '../pages/CRC/Contacts';
import CrcPendencies from '../pages/CRC/Pendencies';
import CrcReports from '../pages/CRC/Reports';
import CrcReportsPrint from '../pages/CRC/Reports/Print';
import { Screens } from '../shared/enums/screens.enum';

interface RouteProtectedProps {
  children: React.ReactNode;
  isPrivate?: boolean;
  requiredScreens?: string[];
}

const ProtectedRoute: React.FC<RouteProtectedProps> = ({
  children,
  isPrivate,
  requiredScreens,
}) => {
  const { user, screenFind } = useAuth();
  const location = useLocation();

  if (isPrivate === !!user) {
    if (isPrivate && requiredScreens?.length && !screenFind(requiredScreens)) {
      return (
        <Navigate
          to="/access-denied"
          replace
          state={{ from: location.pathname }}
        />
      );
    }

    return <>{children}</>;
  }

  return <Navigate to={{ pathname: isPrivate ? '/' : '/dashboard' }} replace />;
};

const routes: React.FC = () => {
  return (
    <BrowserRouter future={routerFutureFlags}>
      <Routes>
        <Route
          path="/"
          index
          element={
            <ProtectedRoute isPrivate={false}>
              <SignIn />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:tenant/confirm-appointment/:schedule_id/:token"
          element={<ScheduleConfirm />}
        />
        <Route
          path="/confirm-appointment/:schedule_id/:token"
          element={<ScheduleConfirm />}
        />
        <Route path="/:tenant/schedule-online" element={<PublicSchedule />} />
        <Route path="/schedule-online" element={<PublicSchedule />} />
        <Route path="/:tenant/portal" element={<PatientLogin />} />
        <Route path="/portal" element={<PatientLogin />} />
        <Route path="/:tenant/portal/painel" element={<PatientPortal />} />
        <Route path="/portal/painel" element={<PatientPortal />} />
        <Route
          path="/:tenant/portal/health-certificate/:id/print"
          element={<PatientHealthCertificatePrint />}
        />
        <Route
          path="/portal/health-certificate/:id/print"
          element={<PatientHealthCertificatePrint />}
        />
        <Route
          path="/:tenant/portal/document/:id/print"
          element={<PatientDocumentPrint />}
        />
        <Route
          path="/portal/document/:id/print"
          element={<PatientDocumentPrint />}
        />
        <Route
          path="/:tenant/portal/prescription/:id/print"
          element={<PatientDocumentPrint />}
        />
        <Route
          path="/portal/prescription/:id/print"
          element={<PatientDocumentPrint />}
        />
        <Route path="/validate-signature" element={<SignatureValidation />} />
        <Route path="/404" element={<PageNotFound />} />
        <Route
          path="*"
          element={
            <SideNav>
              <Routes_Protected_Content />
            </SideNav>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

const Routes_Protected_Content: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute isPrivate={true}>
            <Navigate replace to="/dashboard" />
          </ProtectedRoute>
        }
      />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute isPrivate={true}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedules"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.schedulesView]}
              >
                <Schedules />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedules_status"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.schedulesStatusView]}
              >
                <SchedulesStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedules/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.schedulesView]}
              >
                <SchedulesPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/holidays"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.holidaysView]}
              >
                <Holidays />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute isPrivate={true}>
                <Clients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <ProtectedRoute isPrivate={true}>
                <ClientCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/reports/list"
            element={
              <ProtectedRoute isPrivate={true}>
                <ClientReportList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/reports/birthdays"
            element={
              <ProtectedRoute isPrivate={true}>
                <ClientReportBirthdays />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/reports/list/print"
            element={
              <ProtectedRoute isPrivate={true}>
                <ClientReportListPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/reports/birthdays/print"
            element={
              <ProtectedRoute isPrivate={true}>
                <ClientReportBirthdaysPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/addresses/:client_id"
            element={
              <ProtectedRoute isPrivate={true}>
                <Addresses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial_plans"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsPlansView]}
              >
                <FinancialPlans />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments_methods"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.paymentsMethodsView]}
              >
                <PaymentsMethods />
              </ProtectedRoute>
            }
          />
          <Route
            path="/taxes/:payment_method_id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.taxesView]}
              >
                <Taxes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/procedures"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.proceduresView]}
              >
                <Procedures />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financials/:client_id?"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <Financials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/movements"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.movementsFinancialsView]}
              >
                <MovementsFinancials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/accounts-payable"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <AccountsPayable />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/accounts-receivable"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <AccountsReceivable />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/advanced"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <FinancialAdvancedReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/advanced/:reportKey"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <FinancialAdvancedReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/commission-closing"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <CommissionClosingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/delinquency"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <DelinquencyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/dre"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <FinancialDRE />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/reports/cash-flow"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <FinancialCashFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/cash-register"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.cashRegisterView]}
              >
                <CashRegisterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financial/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.financialsView]}
              >
                <FinancialCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sale"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.quickSaleView]}
              >
                <QuickSalePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.salesView]}
              >
                <SalesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/professionals"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.professionalsView]}
              >
                <Professionals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/professionals/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.professionalsView]}
              >
                <ProfessionalCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.usersView]}
              >
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.usersView]}
              >
                <UserCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/access_groups"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.accessGroupsView]}
              >
                <AccessGroup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/covenants"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.covenantsView]}
              >
                <Covenants />
              </ProtectedRoute>
            }
          />
          <Route
            path="/terminologies"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.terminologiesView]}
              >
                <Terminologies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guides"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.guidesView]}
              >
                <Guides />
              </ProtectedRoute>
            }
          />
          <Route
            path="/guides_status"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.guidesStatusView]}
              >
                <GuidesStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatments/:client_id?"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <Treatments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatments/reports/production"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <ProceduresReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatments/reports/treatments"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <TreatmentsReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatments/reports/procedures"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <TreatmentsProceduresReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatment/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <TratmentCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatment/:id/approval"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <TreatmentApprovalOrNot />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatment/:id/evolution"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <TreatmentEvolution />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatment/:id/evolution/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <TreatmentEvolutionPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/treatment/:id/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.treatmentsView]}
              >
                <TreatmentPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dentals/:client_id?"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.odontologyView]}
              >
                <Dental />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dental/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.odontologyView]}
              >
                <DentalCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dental/:id/approval"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.odontologyView]}
              >
                <DentalTreatmentApprovalOrNot />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dental/:id/evolution"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.odontologyView]}
              >
                <DentalTreatmentEvolution />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dental/:id/evolution/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.odontologyView]}
              >
                <DentalTreatmentEvolutionPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dental/:id/procedure/:itemId/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.odontologyView]}
              >
                <DentalTreatmentProcedurePrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dental/:id/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.odontologyView]}
              >
                <DentalTreatmentPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/models_health_certificates"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.modelsHealthCertificatesView]}
              >
                <ModelsHealthCertificates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/model_health_certificate/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.modelsHealthCertificatesView]}
              >
                <ModelHealthCertificateCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/models_documents"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.modelsDocumentsView]}
              >
                <ModelsDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/models_prescriptions"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.modelsDocumentsView]}
              >
                <ModelsDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/model_document/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.modelsDocumentsView]}
              >
                <ModelDocumentCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/model_prescription/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.modelsDocumentsView]}
              >
                <ModelDocumentCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/health_certificates/:client_id?"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.healthCertificatesView]}
              >
                <HealthCertificates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/health_certificate/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.healthCertificatesView]}
              >
                <HealthCertificateCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/health_certificate/:id/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.healthCertificatesView]}
              >
                <HealthCertificatePrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/document/:id/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.documentsView]}
              >
                <DocumentPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prescription/:id/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.documentsView]}
              >
                <DocumentPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents/:client_id?"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.documentsView]}
              >
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prescriptions/:client_id?"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.documentsView]}
              >
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/document/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.documentsView]}
              >
                <DocumentCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prescription/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.documentsView]}
              >
                <DocumentCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/models_forms"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.modelsFormsView]}
              >
                <ModelsForms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/forms/:client_id?"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.formsView]}
              >
                <Forms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/forms/:client_id/charts"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.formsView]}
              >
                <FormCharts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/form/:id/quiz"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.formsView]}
              >
                <FormClientQuiz />
              </ProtectedRoute>
            }
          />
          <Route
            path="/form/:id/evolution"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.formsView]}
              >
                <FormClientEvolution />
              </ProtectedRoute>
            }
          />
          <Route
            path="/form/:id/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.formsView]}
              >
                <FormClientPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/model_form/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.modelsFormsView]}
              >
                <ModelFormCreateEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/specialties"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.specialtiesView]}
              >
                <Specialties />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products_categories"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.productsCategoriesView]}
              >
                <ProductsCategories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.productsView]}
              >
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventories"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.inventoriesView]}
              >
                <Inventories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory_entries"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.inventoriesView]}
              >
                <InventoryEntries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory_outputs"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.inventoriesView]}
              >
                <InventoryOutputs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/connections"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.whatsAppView]}
              >
                <WhatsappConnections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/whatsapp/usage"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.whatsAppView]}
              >
                <WhatsappUsage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/whatsapp/monitoring"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.whatsAppView]}
              >
                <WhatsappMonitoring />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/campaigns"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crmCampaignsView]}
              >
                <CrmCampaigns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/campaigns/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crmCampaignsView]}
              >
                <CrmCampaignsEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/templates"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crmTemplatesView]}
              >
                <CrmTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/templates/:id"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crmTemplatesView]}
              >
                <CrmTemplatesEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/messaging"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crmMessagingView, Screens.crmCampaignsView]}
              >
                <CrmMessaging />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crc/dashboard"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crcDashboardView]}
              >
                <CrcDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crc/contacts"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crcContactsView]}
              >
                <CrcContacts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crc/pendencies"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crcPendenciesView]}
              >
                <CrcPendencies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crc/reports"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crcReportsView]}
              >
                <CrcReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crc/reports/print"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.crcReportsView]}
              >
                <CrcReportsPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company"
            element={
              <ProtectedRoute
                isPrivate={true}
                requiredScreens={[Screens.companyView]}
              >
                <Company />
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <ProtectedRoute isPrivate={true}>
                <Companies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute isPrivate={true}>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/access-denied"
            element={
              <ProtectedRoute isPrivate={true}>
                <AccessDenied />
              </ProtectedRoute>
            }
          />
          <Route path="/404" element={<PageNotFound />} />
          <Route path="*" element={<Navigate replace to="/404" />} />
        </Routes>
  );
};

export default routes;
export { Routes_Protected_Content };
